import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchTaxDeclarationProgress, upsertTaxDeclarationProgress } from '../taxDeclarationApi';
import type { WheelDateValue } from '../../components/WheelDatePicker';
import { fetchOtherIncomeMemberCandidates } from './otherIncomeEarnerApi';
import { otherIncomeEarnerFromApi, mergeEntryIntoSnapshot, otherIncomeEarnerSnapshotForPersist } from './otherIncomeEarnerMapper';
import {
  addAnotherAnswerForReview,
  buildResumeStep,
  clearDraftFromStep,
  committedStepsForReview,
  isDraftStepAnswered,
  isSkippedStep,
  landOnStepAfterUndo,
  nextVisibleStep,
  otherIncomeEarnerCursor,
  parseOtherIncomeEarnerCursor,
  showUndoForStep,
  stepIndex,
  stepQuestionKey,
  changedDuringYearQuestion,
  undoTargetStep,
} from './otherIncomeEarnerSteps';
import type {
  EarnerRole,
  FamilyMemberCandidate,
  MemberToEarnerRelationship,
  OtherIncomeChangeReason,
  OtherIncomeClaimDraft,
  OtherIncomeClaimEntry,
  OtherIncomeEarnerSnapshot,
  OtherIncomeEarnerStep,
  YesNo,
} from './otherIncomeEarnerTypes';
import {
  emptyOtherIncomeClaimDraft,
  emptyOtherIncomeEarnerSnapshot,
  MAX_OTHER_INCOME_CLAIM_ENTRIES,
  MEMBER_LOOP_STEPS,
  OTHER_INCOME_EARNER_STEP_ORDER,
} from './otherIncomeEarnerTypes';
import { formatOtherIncomeChangeReasonDisplay } from './otherIncomeEarnerDisplay';
import { retirementAllowanceCursor } from '../retirementAllowance/retirementAllowanceSteps';

function defaultChangeDateWheel(): WheelDateValue {
  const today = new Date();
  return { year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate() };
}

function wheelFromIso(iso: string, fallback: WheelDateValue): WheelDateValue {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return fallback;
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

function isoFromWheel(w: WheelDateValue): string {
  return `${w.year}-${String(w.month).padStart(2, '0')}-${String(w.day).padStart(2, '0')}`;
}

export function useOtherIncomeEarnerFlow(
  email: string | null,
  taxYear: number,
  t: (key: string) => string,
  onSectionDoneChange: (done: boolean) => void
) {
  const [isLoading, setIsLoading] = useState(true);
  const [saveError, setSaveError] = useState(false);
  const [step, setStep] = useState<OtherIncomeEarnerStep>('intro');
  const [snap, setSnap] = useState<OtherIncomeEarnerSnapshot>(emptyOtherIncomeEarnerSnapshot());
  const [draft, setDraft] = useState<OtherIncomeClaimDraft>(emptyOtherIncomeClaimDraft());
  const [candidates, setCandidates] = useState<FamilyMemberCandidate[]>([]);
  const [committed, setCommitted] = useState<Set<OtherIncomeEarnerStep>>(() => new Set());
  const [reviewMode, setReviewMode] = useState(false);
  const [addAnotherAnswer, setAddAnotherAnswer] = useState<YesNo | ''>('');
  const [changeDateWheel, setChangeDateWheel] = useState<WheelDateValue>(defaultChangeDateWheel);
  const onDoneRef = useRef(onSectionDoneChange);
  onDoneRef.current = onSectionDoneChange;
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const reviewModeActive = reviewMode || step === 'review';
  const isOtherIncomeBlockComplete = step === 'review' && reviewModeActive;
  const isComplete = isOtherIncomeBlockComplete;

  const usedFamilyIds = useCallback(
    () => new Set(snap.entries.map((e) => e.empFamilyId)),
    [snap.entries]
  );

  const availableCandidates = useCallback(() => {
    const used = usedFamilyIds();
    if (draft.empFamilyId > 0) used.delete(draft.empFamilyId);
    return candidates.filter((c) => !used.has(c.empFamilyId));
  }, [candidates, draft.empFamilyId, usedFamilyIds]);

  const markCommitted = useCallback((s: OtherIncomeEarnerStep) => {
    setCommitted((prev) => new Set(prev).add(s));
  }, []);

  useEffect(() => {
    if (step !== 'changeDate') return;
    setChangeDateWheel(wheelFromIso(draft.changeDate, defaultChangeDateWheel()));
  }, [draft.changeDate, step]);

  const persist = useCallback(
    async (opts: {
      nextStep?: OtherIncomeEarnerStep | null;
      snapPatch?: Partial<OtherIncomeEarnerSnapshot>;
      sectionDone?: boolean;
      draftOverride?: OtherIncomeClaimDraft;
      syncOtherIncome?: boolean;
    }) => {
      if (!email?.trim()) return false;
      const merged = { ...snap, ...(opts.snapPatch ?? {}) };
      setSnap(merged);
      const draftNow = opts.draftOverride ?? draftRef.current;
      const syncOtherIncome =
        opts.syncOtherIncome ??
        (opts.snapPatch !== undefined ||
          draftNow.empFamilyId > 0 ||
          merged.anyClaimedByOther === 'yes' ||
          merged.anyClaimedByOther === 'no');
      const saved = await upsertTaxDeclarationProgress({
        email: email.trim(),
        taxYear,
        ...(opts.nextStep !== undefined
          ? { currentStepId: otherIncomeEarnerCursor(opts.nextStep ?? 'review') }
          : {}),
        ...(opts.sectionDone !== undefined ? { sectionOtherIncomeDone: opts.sectionDone } : {}),
        ...(syncOtherIncome
          ? { otherIncomeEarner: otherIncomeEarnerSnapshotForPersist(merged, draftNow) }
          : {}),
      });
      if (!saved) {
        setSaveError(true);
        return false;
      }
      setSaveError(false);
      if (saved.otherIncomeEarner) setSnap(otherIncomeEarnerFromApi(saved.otherIncomeEarner));
      if (opts.sectionDone === true) onDoneRef.current(true);
      if (opts.sectionDone === false) onDoneRef.current(false);
      return true;
    },
    [email, snap, taxYear]
  );

  useEffect(() => {
    if (!email?.trim()) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      const [progress, cands] = await Promise.all([
        fetchTaxDeclarationProgress(email.trim(), taxYear),
        fetchOtherIncomeMemberCandidates(email.trim()),
      ]);
      if (cancelled) return;
      setCandidates(cands);
      const loaded = otherIncomeEarnerFromApi(progress?.otherIncomeEarner);
      setSnap(loaded);
      const sectionDone = progress?.sectionOtherIncomeDone === true;
      const cursor = parseOtherIncomeEarnerCursor(progress?.currentStepId);
      const resume =
        cursor ??
        buildResumeStep(loaded, emptyOtherIncomeClaimDraft(), sectionDone);
      const isRetirementPhase = (progress?.currentStepId ?? '').startsWith('retirementAllowance:');
      if (sectionDone || isRetirementPhase) {
        setReviewMode(true);
        setStep('review');
        setCommitted(committedStepsForReview(loaded, cursor));
        setAddAnotherAnswer(addAnotherAnswerForReview(loaded, cursor));
      } else {
        setReviewMode(false);
        setStep(resume);
        const comm = new Set<OtherIncomeEarnerStep>(['intro']);
        if (loaded.anyClaimedByOther !== '') comm.add('anyClaimedGate');
        setCommitted(comm);
        setAddAnotherAnswer('');
      }
      setDraft(emptyOtherIncomeClaimDraft());
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [email, taxYear]);

  const skipIntro = useCallback(async () => {
    markCommitted('intro');
    setStep('anyClaimedGate');
    await persist({ nextStep: 'anyClaimedGate' });
  }, [markCommitted, persist]);

  const handleAnyClaimed = useCallback(
    async (v: YesNo) => {
      const patch: Partial<OtherIncomeEarnerSnapshot> = {
        anyClaimedByOther: v,
        entries: v === 'no' ? [] : snap.entries,
      };
      markCommitted('anyClaimedGate');
      setAddAnotherAnswer('');
      const next = v === 'yes' ? 'pickMember' : 'review';
      setSnap((s) => ({ ...s, ...patch }));
      setStep(next);
      if (v === 'no') {
        setReviewMode(true);
        await persist({ nextStep: 'review', snapPatch: patch, sectionDone: false });
        return;
      }
      setReviewMode(false);
      setDraft(emptyOtherIncomeClaimDraft());
      await persist({ nextStep: next, snapPatch: patch, sectionDone: false });
    },
    [markCommitted, persist, snap.entries]
  );

  const handlePickMember = useCallback(
    async (c: FamilyMemberCandidate) => {
      const nextDraft: OtherIncomeClaimDraft = {
        ...emptyOtherIncomeClaimDraft(),
        empFamilyId: c.empFamilyId,
        memberFullName: c.fullName,
        memberRelationship: c.relationship,
        earnerName: c.otherEarnerName,
        earnerAddress: c.otherEarnerAddress,
      };
      setDraft(nextDraft);
      markCommitted('pickMember');
      setStep('earnerRole');
      await persist({ nextStep: 'earnerRole', draftOverride: nextDraft });
    },
    [markCommitted, persist]
  );

  const advanceDraft = useCallback(
    async (from: OtherIncomeEarnerStep, patch: Partial<OtherIncomeClaimDraft>) => {
      const merged = { ...draft, ...patch };
      setDraft(merged);
      markCommitted(from);
      const next = nextVisibleStep(from, snap, merged);
      if (!next) return;
      if (next === 'review') {
        const entries = mergeEntryIntoSnapshot(snap, merged);
        const snapPatch = { entries };
        setSnap((s) => ({ ...s, ...snapPatch }));
        setDraft(emptyOtherIncomeClaimDraft());
        setReviewMode(true);
        setStep('review');
        await persist({ nextStep: 'review', snapPatch, sectionDone: false });
        return;
      }
      setStep(next);
      await persist({ nextStep: next, draftOverride: merged });
    },
    [draft, markCommitted, persist, snap]
  );

  const handleEarnerRole = useCallback(
    async (v: EarnerRole) => {
      const patch: Partial<OtherIncomeClaimDraft> = { earnerRole: v };
      if (v !== 'other') patch.earnerRoleOther = '';
      await advanceDraft('earnerRole', patch);
    },
    [advanceDraft]
  );

  const handleEarnerRoleOther = useCallback(
    async (v: string) => {
      if (!v.trim()) return;
      await advanceDraft('earnerRoleOther', { earnerRoleOther: v.trim() });
    },
    [advanceDraft]
  );

  const handleEarnerName = useCallback(
    async (v: string) => {
      if (!v.trim()) return;
      await advanceDraft('earnerName', { earnerName: v.trim() });
    },
    [advanceDraft]
  );

  const handleEarnerAddress = useCallback(
    async (v: string) => {
      if (!v.trim()) return;
      await advanceDraft('earnerAddress', { earnerAddress: v.trim() });
    },
    [advanceDraft]
  );

  const handleMemberRelationship = useCallback(
    async (v: MemberToEarnerRelationship) => {
      await advanceDraft('memberToEarnerRelationship', { memberToEarnerRelationship: v });
    },
    [advanceDraft]
  );

  const handleChangedDuringYear = useCallback(
    async (v: YesNo) => {
      const patch: Partial<OtherIncomeClaimDraft> = { changedDuringYear: v };
      if (v === 'no') {
        patch.changeDate = '';
        patch.changeReason = '';
        patch.changeReasonOther = '';
      }
      const merged = { ...draft, ...patch };
      setDraft(merged);
      markCommitted('changedDuringYear');
      const next = nextVisibleStep('changedDuringYear', snap, merged);
      if (!next) return;
      if (next === 'review') {
        const entries = mergeEntryIntoSnapshot(snap, merged);
        const snapPatch = { entries };
        setSnap((s) => ({ ...s, ...snapPatch }));
        setDraft(emptyOtherIncomeClaimDraft());
        setReviewMode(true);
        setStep('review');
        await persist({ nextStep: 'review', snapPatch, sectionDone: false });
        return;
      }
      setStep(next);
      await persist({ nextStep: next, draftOverride: merged });
    },
    [draft, markCommitted, persist, snap]
  );

  const handleChangeDate = useCallback(
    async (iso: string) => {
      if (!iso.trim()) return;
      await advanceDraft('changeDate', { changeDate: iso.trim() });
    },
    [advanceDraft]
  );

  const handleSendChangeDate = useCallback(async () => {
    await handleChangeDate(isoFromWheel(changeDateWheel));
  }, [changeDateWheel, handleChangeDate]);

  const handleChangeReason = useCallback(
    async (v: OtherIncomeChangeReason) => {
      const patch: Partial<OtherIncomeClaimDraft> = { changeReason: v };
      if (v !== 'other') patch.changeReasonOther = '';
      const merged = { ...draft, ...patch };
      setDraft(merged);
      markCommitted('changeReason');
      const next = nextVisibleStep('changeReason', snap, merged);
      if (!next) return;
      if (next === 'review') {
        const entries = mergeEntryIntoSnapshot(snap, merged);
        const snapPatch = { entries };
        setSnap((s) => ({ ...s, ...snapPatch }));
        setDraft(emptyOtherIncomeClaimDraft());
        setReviewMode(true);
        setStep('review');
        await persist({ nextStep: 'review', snapPatch, sectionDone: false });
        return;
      }
      setStep(next);
      await persist({ nextStep: next, draftOverride: merged });
    },
    [draft, markCommitted, persist, snap]
  );

  const handleChangeReasonOther = useCallback(
    async (v: string) => {
      if (!v.trim()) return;
      await advanceDraft('changeReasonOther', { changeReasonOther: v.trim() });
    },
    [advanceDraft]
  );

  const handleAddAnother = useCallback(
    async (v: YesNo) => {
      setAddAnotherAnswer(v);
      const entries = mergeEntryIntoSnapshot(snap, draft);
      const wouldBeCount = entries.filter((e) => e.empFamilyId > 0).length;
      if (v === 'yes') {
        if (wouldBeCount >= MAX_OTHER_INCOME_CLAIM_ENTRIES) {
          return;
        }
        const patch = { entries };
        setSnap((s) => ({ ...s, ...patch }));
        setDraft(emptyOtherIncomeClaimDraft());
        markCommitted('addAnother');
        setStep('pickMember');
        await persist({ nextStep: 'pickMember', snapPatch: patch });
        return;
      }
      const patch = { entries };
      markCommitted('addAnother');
      setReviewMode(true);
      setStep('review');
      setSnap((s) => ({ ...s, ...patch }));
      await persist({ nextStep: 'review', snapPatch: patch, sectionDone: false });
    },
    [draft, markCommitted, persist, snap]
  );

  const finishReview = useCallback(async () => {
    await persist({ nextStep: 'review', sectionDone: false });
  }, [persist]);

  const enterReviewFromMenu = useCallback(() => {
    setReviewMode(true);
    setStep('review');
    setCommitted(committedStepsForReview(snap, 'review'));
    setAddAnotherAnswer(addAnotherAnswerForReview(snap, 'review'));
  }, [snap]);

  const handoffDoneRef = useRef(false);
  const prevOtherIncomeCompleteRef = useRef(false);
  useEffect(() => {
    const becameComplete = isOtherIncomeBlockComplete && !prevOtherIncomeCompleteRef.current;
    prevOtherIncomeCompleteRef.current = isOtherIncomeBlockComplete;
    if (!isOtherIncomeBlockComplete) {
      handoffDoneRef.current = false;
    }
    if (!becameComplete || handoffDoneRef.current) return;
    const id = window.setTimeout(() => {
      if (handoffDoneRef.current) return;
      handoffDoneRef.current = true;
      let cancelled = false;
      (async () => {
        if (!email?.trim()) return;
        const saved = await upsertTaxDeclarationProgress({
          email: email.trim(),
          taxYear,
          currentStepId: retirementAllowanceCursor('intro'),
          otherIncomeEarner: snap,
          sectionOtherIncomeDone: false,
        });
        if (cancelled) return;
      })();
    }, 650);
    return () => window.clearTimeout(id);
  }, [email, isOtherIncomeBlockComplete, snap, taxYear]);

  const handleUndo = useCallback(
    async (undoFromStep?: OtherIncomeEarnerStep) => {
      const wasInReview = reviewModeActive;
      onDoneRef.current(false);
      setReviewMode(false);

      const undoFrom =
        undoFromStep ??
        undoTargetStep(step, draft, snap, committed, reviewModeActive) ??
        (step === 'review' && snap.entries.length > 0
          ? ('addAnother' as OtherIncomeEarnerStep)
          : step === 'review'
            ? ('anyClaimedGate' as OtherIncomeEarnerStep)
            : null);
      if (!undoFrom) return;

      if (undoFrom === 'anyClaimedGate') {
        const patch: Partial<OtherIncomeEarnerSnapshot> = {
          anyClaimedByOther: '',
          entries: [],
        };
        setSnap((s) => ({ ...s, ...patch }));
        setDraft(emptyOtherIncomeClaimDraft());
        setStep('anyClaimedGate');
        setCommitted(new Set(['intro']));
        setAddAnotherAnswer('');
        await persist({ nextStep: 'anyClaimedGate', snapPatch: patch, sectionDone: false });
        return;
      }

      if (undoFrom === 'addAnother' && snap.entries.length > 0) {
        const entries = snap.entries.slice(0, -1);
        const last = snap.entries[snap.entries.length - 1];
        const patch = { entries };
        setSnap((s) => ({ ...s, ...patch }));
        setDraft({ ...last });
        setStep('addAnother');
        setCommitted((prev) => {
          const next = new Set(prev);
          next.delete('addAnother');
          return next;
        });
        setAddAnotherAnswer('');
        await persist({ nextStep: 'addAnother', snapPatch: patch, sectionDone: false });
        return;
      }

      if (
        wasInReview &&
        undoFrom !== 'anyClaimedGate' &&
        undoFrom !== 'addAnother' &&
        MEMBER_LOOP_STEPS.includes(undoFrom) &&
        snap.entries.length > 0
      ) {
        const last = snap.entries[snap.entries.length - 1];
        const landOn = undoFrom;
        const clearPatch = clearDraftFromStep(landOn);
        const entries = snap.entries.slice(0, -1);
        const mergedDraft = { ...last, ...clearPatch };
        setSnap((s) => ({ ...s, entries }));
        setDraft(mergedDraft);
        setStep(landOn);
        setCommitted((prev) => {
          const next = new Set(prev);
          for (const s of OTHER_INCOME_EARNER_STEP_ORDER) {
            if (stepIndex(s) >= stepIndex(landOn)) next.delete(s);
          }
          return next;
        });
        setAddAnotherAnswer('');
        await persist({ nextStep: landOn, snapPatch: { entries }, sectionDone: false });
        return;
      }

      const landOn = landOnStepAfterUndo(undoFrom, draft) ?? undoFrom;
      const clearPatch = clearDraftFromStep(landOn);
      const mergedDraft = { ...draft, ...clearPatch };
      setDraft(mergedDraft);
      setStep(landOn);
      setCommitted((prev) => {
        const next = new Set(prev);
        for (const s of OTHER_INCOME_EARNER_STEP_ORDER) {
          if (stepIndex(s) >= stepIndex(landOn)) next.delete(s);
        }
        return next;
      });
      await persist({ nextStep: landOn, sectionDone: false, draftOverride: mergedDraft });
    },
    [committed, draft, persist, reviewModeActive, snap, step]
  );

  const canShowUndoForStep = useCallback(
    (s: OtherIncomeEarnerStep) =>
      showUndoForStep(s, step, draft, snap, committed.has(s), reviewModeActive, committed),
    [committed, draft, reviewModeActive, snap, step]
  );

  const getUndoTargetStep = useCallback(
    () => undoTargetStep(step, draft, snap, committed, reviewModeActive),
    [committed, draft, reviewModeActive, snap, step]
  );

  const userAnswerForStep = useCallback(
    (s: OtherIncomeEarnerStep, entry?: OtherIncomeClaimEntry): string => {
      const d = entry ?? draft;
      switch (s) {
        case 'anyClaimedGate':
          return t(
            snap.anyClaimedByOther === 'yes'
              ? 'submitDocuments.dependentDeduction.otherIncomeEarner.yes'
              : 'submitDocuments.dependentDeduction.otherIncomeEarner.no'
          );
        case 'pickMember':
          return `${d.memberFullName} (${t(`submitDocuments.familyInfo.relationship.${d.memberRelationship}`)})`;
        case 'earnerRole':
          return d.earnerRole !== ''
            ? t(`submitDocuments.dependentDeduction.otherIncomeEarner.earnerRole.${d.earnerRole}`)
            : '';
        case 'earnerRoleOther':
          return d.earnerRoleOther;
        case 'earnerName':
          return d.earnerName;
        case 'earnerAddress':
          return d.earnerAddress;
        case 'memberToEarnerRelationship':
          return t(
            `submitDocuments.dependentDeduction.otherIncomeEarner.memberRelationship.${d.memberToEarnerRelationship}`
          );
        case 'changedDuringYear':
          return t(
            d.changedDuringYear === 'yes'
              ? 'submitDocuments.dependentDeduction.otherIncomeEarner.yes'
              : 'submitDocuments.dependentDeduction.otherIncomeEarner.no'
          );
        case 'changeDate':
          return d.changeDate;
        case 'changeReason':
          return formatOtherIncomeChangeReasonDisplay(t, d.changeReason, d.changeReasonOther);
        case 'changeReasonOther':
          return d.changeReasonOther;
        case 'addAnother':
          return addAnotherAnswer === 'yes'
            ? t('submitDocuments.dependentDeduction.otherIncomeEarner.yes')
            : addAnotherAnswer === 'no'
              ? t('submitDocuments.dependentDeduction.otherIncomeEarner.no')
              : '';
        default:
          return '';
      }
    },
    [addAnotherAnswer, draft, snap.anyClaimedByOther, t]
  );

  const resetRetirementHandoff = useCallback(() => {
    handoffDoneRef.current = false;
    prevOtherIncomeCompleteRef.current = false;
  }, []);

  return {
    t,
    taxYear,
    step,
    snap,
    draft,
    setDraft,
    candidates,
    availableCandidates,
    committed,
    isLoading,
    saveError,
    reviewMode: reviewModeActive,
    isComplete,
    isOtherIncomeBlockComplete,
    skipIntro,
    handleAnyClaimed,
    handlePickMember,
    handleEarnerRole,
    handleEarnerRoleOther,
    handleEarnerName,
    handleEarnerAddress,
    handleMemberRelationship,
    handleChangedDuringYear,
    changeDateWheel,
    setChangeDateWheel,
    handleChangeDate,
    handleSendChangeDate,
    handleChangeReason,
    handleChangeReasonOther,
    handleAddAnother,
    finishReview,
    enterReviewFromMenu,
    handleUndo,
    canShowUndoForStep,
    undoTargetStep: getUndoTargetStep,
    undoLabel: t('submitDocuments.chat.undo'),
    userAnswerForStep,
    stepQuestionKey: (s: OtherIncomeEarnerStep) => stepQuestionKey(s, taxYear),
    changedDuringYearQuestion: () => changedDuringYearQuestion(t, taxYear),
    isCommitted: (s: OtherIncomeEarnerStep) => committed.has(s),
    isSkippedStep: (s: OtherIncomeEarnerStep) => isSkippedStep(s, draft),
    canAddAnotherMember:
      snap.entries.length + (draft.empFamilyId > 0 ? 1 : 0) < MAX_OTHER_INCOME_CLAIM_ENTRIES,
    isDraftStepAnswered: (s: OtherIncomeEarnerStep) => isDraftStepAnswered(s, draft),
    memberLoopSteps: MEMBER_LOOP_STEPS,
    stepOrder: OTHER_INCOME_EARNER_STEP_ORDER,
    resetRetirementHandoff,
  };
}

export type OtherIncomeEarnerFlow = ReturnType<typeof useOtherIncomeEarnerFlow>;
